import { User } from 'api-clients/identity-server-api-client';
import { HeaderService } from 'src/app/header/header.service';
import { globalWindow } from '../window-globals';

export interface FreshchatConfig {
  isOpen: boolean;
  restoreId: number | null;
}

// https://developers.freshchat.com/web-sdk/#user-events
interface FreshchatMessage {
  success: boolean,
  status: string,
  data: { restoreId: number },
}

function getFreshchatConfig(): FreshchatConfig {
  const current = localStorage.getItem('freshchat');
  let config: FreshchatConfig | null;
  try {
    config = current ? JSON.parse(current) : null;
  } catch {
    config = null;
  }

  return {
    isOpen: false,
    restoreId: null,
    ...config,
  };
};

function updateFreshchatConfig(config: Partial<FreshchatConfig>): void {
  const newConfig = {
    ...getFreshchatConfig(),
    ...config,
  };
  localStorage.setItem('freshchat', JSON.stringify(newConfig));
};

export function initializeFreshchat(user: User, headerService: HeaderService): void {
  const scriptElement = document.createElement('script');
  scriptElement.id = 'freshchat-js-sdk';
  scriptElement.async = true;
  scriptElement.src = 'https://wchat.freshchat.com/js/widget.js';
  scriptElement.onload = (): void => {

    const config = getFreshchatConfig();

    globalWindow.fcWidget.init({
      token: '68beb920-591c-47e2-ac30-8edea76d51fa',
      host: 'https://wchat.freshchat.com',
      externalId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      restoreId: config.restoreId,
      config: {
        headerProperty: { hideChatButton: true },
      },
    });

    globalWindow.fcWidget.on('widget:closed', () => {
      updateFreshchatConfig({ isOpen: false });
      window.focus();
    });

    globalWindow.fcWidget.on('widget:opened', () => {
      updateFreshchatConfig({ isOpen: true });
      headerService.updateFreshChatUnreadCount.next(0);
    });

    globalWindow.fcWidget.on('user:created', (response: FreshchatMessage) => {
      if (response.success) {
        updateFreshchatConfig({ restoreId: response.data.restoreId });
      }
    });

    // not sure why the fcWidget.on('conversation:resolved') is not working, so listening for the message event directly
    globalWindow.addEventListener('message', (messageEvent: MessageEvent) => {
      // when we see the conversation_resolved event, we don't want to restore the session next time
      if (messageEvent.origin === 'https://wchat.freshchat.com' && messageEvent.data?.action === 'conversation_resolved') {
        updateFreshchatConfig({ restoreId: null });
        globalWindow.fcWidget.user.isExists().then(
          (response: { data: boolean }) => response.data || globalWindow.fcWidget.user.clear());
      }
    });

    globalWindow.fcWidget.on('unreadCount:notify', (response: { count: number }) => {
      // show notification bubble on help widget if closed
      if (!globalWindow.fcWidget.isOpen()) {
        // show notification bubble on help widget
        headerService.updateFreshChatUnreadCount.next(response.count);
      }
    });

    if (config.isOpen && config.restoreId) {
      globalWindow.fcWidget.open({ name: 'Support' });
    }
  };
  document.head.appendChild(scriptElement);
}

export function toggleFreshchatHelp(): void {
  // Freshchat offers no toggle support so we check isOpen
  if (globalWindow.fcWidget) {
    if (globalWindow.fcWidget.isOpen()) {
      globalWindow.fcWidget.close();
      globalWindow.fcWidget.hide();
    } else {
      const config = getFreshchatConfig();
      const data = config.restoreId ? { name: 'Support' } : {};
      globalWindow.fcWidget.open(data);
      globalWindow.fcWidget.show();
    }
  } else {
    globalWindow.open('https://www.ezlynx.com/support', '_blank', 'noopener');
  }
}
